import { api, type Solicitud } from "../api";
import { estado } from "../estado";
import { esc, LOGO, CANDADO, Fechas, modal, toast, primerNombre } from "../ui";
import type { Vista, Limpieza } from "./tipos";

// Personajes desbloqueables por puntaje (docs/flujos/F01). Cosméticos: no tocan permisos.
const SKINS = [
  { nombre: "Corredor", req: 0 }, { nombre: "Robot", req: 500 }, { nombre: "Criatura", req: 1500 },
  { nombre: "Alien", req: 3000 }, { nombre: "Makai", req: 6000 },
];
const ESTADO_SOL: Record<string, string> = { abierta: "En revisión de IT", concedida: "Concedida", parcial: "Concedida parcialmente", negada: "Negada" };

export async function sinAcceso(alSalir: () => Promise<void>, refrescar: () => void, alSalirDeVista: Limpieza): Promise<Vista> {
  const u = estado.usuario()!;
  const [ranking, mias] = await Promise.all([api.ranking().catch(() => []), api.misSolicitudes().catch(() => [] as Solicitud[])]);
  const abierta = mias.find((s) => s.estado === "abierta");
  const ultima = mias[0];
  const mejorInicial = ranking.find((r) => r.nombre === u.nombre)?.puntos ?? 0;

  const estadoHtml = abierta
    ? '<div class="aviso info" style="display:flex;align-items:center;gap:10px"><span class="dot" style="background:var(--warn)"></span> Solicitud abierta · <b>' + esc(ESTADO_SOL[abierta.estado]) + "</b> · abierta " + esc(Fechas.relativa(abierta.creadoEn)) + ". Se aplica en tu siguiente entrada.</div>"
    : ultima && ultima.estado === "negada"
      ? '<div class="aviso crit" style="display:flex;align-items:center;gap:10px">Tu última solicitud fue <b>negada</b>' + (ultima.respuesta?.nota ? ": “" + esc(ultima.respuesta.nota) + "”" : "") + '.</div><button class="btn primario" id="jg-solicitar" style="padding:11px 18px">Volver a solicitar</button>'
      : '<button class="btn primario" id="jg-solicitar" style="padding:11px 18px">Solicitar acceso a IT</button>';

  return {
    full:
      '<div class="juego-wrap"><div class="juego">' +
        '<div style="text-align:center;max-width:620px"><div class="logo-g" style="width:48px;height:48px;border-radius:14px;background:var(--accent);display:inline-flex;align-items:center;justify-content:center;margin-bottom:14px">' + LOGO(24) + "</div>" +
        '<div style="font-size:26px;font-weight:660;letter-spacing:-.6px">Hola, ' + esc(primerNombre(u.nombre)) + ". Tu cuenta está activa.</div>" +
        '<div style="font-size:13.5px;color:var(--text2);line-height:1.6;margin-top:8px">Pero todavía no tienes accesos asignados: ningún módulo, ningún cliente. Esto no está roto. Mientras IT revisa, juega. <kbd>Espacio</kbd> o clic para saltar los candados.</div></div>' +
        '<canvas id="jg" width="760" height="220"></canvas>' +
        '<div class="skins" id="jg-skins"></div>' +
        '<div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap;justify-content:center">' + estadoHtml + '<button class="btn" data-go="#/academy">Aprender mientras tanto</button><button class="btn" id="jg-salir">Cambiar de usuario</button></div>' +
      "</div>" +
      '<div class="ranking"><div class="cab"><div class="t">Ranking de ' + esc(estado.tenant?.nombre || "la agencia") + '</div><span style="font-size:11px;color:var(--text4)">solo nombres y puntos</span></div><div id="jg-ranking"></div>' +
        '<div class="aviso warn" style="margin:16px">' + CANDADO + " Jugar no concede nada. Los personajes son cosméticos y el ranking no muestra roles, cuentas ni accesos.</div></div>" +
      "</div>",
    despues(root) {
      const cv = root.querySelector<HTMLCanvasElement>("#jg")!; const ctx = cv.getContext("2d")!;
      // Lienzo nítido en pantallas densas: el juego piensa en 760×220.
      const W = 760, H = 220, DPR = Math.min(window.devicePixelRatio || 1, 2);
      cv.width = W * DPR; cv.height = H * DPR; ctx.setTransform(DPR, 0, 0, DPR, 0, 0);

      // Como el dinosaurio de Chrome: espera a la primera tecla, arranca despacio
      // y acelera muy poco a poco. Los obstáculos son candados, a propósito.
      type Obs = { x: number; w: number; h: number };
      let estado: "espera" | "corriendo" | "muerto" = "espera";
      let score = 0, y = 0, vy = 0, obs: Obs[] = [], t = 0, vel = 0, dist = 0, proximo = 0, mejorLocal = mejorInicial;
      let nubes = [0, 1, 2, 3].map((i) => ({ x: i * 230 + 60, y: 30 + (i % 2) * 22, w: 46 + i * 8 }));
      let otros = ranking.filter((r) => r.nombre !== u.nombre);
      const VEL0 = 2.6, VELMAX = 8, GRAV = 0.55, SALTO = 10.2;
      const nivel = (s: number) => { let n = 0; SKINS.forEach((k, i) => { if (s >= k.req) n = i; }); return n; };
      const pintarSkins = () => {
        root.querySelector("#jg-skins")!.innerHTML = SKINS.map((k, i) => {
          const abierto = Math.max(score, mejorLocal) >= k.req; const on = nivel(score) === i;
          return '<div class="skin ' + (on ? "on" : abierto ? "" : "cerrado") + '">' + (abierto ? esc(k.nombre) : "?????") + "<small>" + (k.req ? k.req.toLocaleString("en-US") + " pts" : "de fábrica") + "</small></div>";
        }).join("");
      };
      const pintarRanking = () => {
        const todos = otros.map((r) => ({ n: r.nombre, p: r.puntos, yo: false })).concat([{ n: u.nombre + " (tú)", p: Math.max(score, mejorLocal), yo: true }]).sort((a, b) => b.p - a.p);
        root.querySelector("#jg-ranking")!.innerHTML = todos.map((r, i) => '<div class="r' + (r.yo ? " yo" : "") + '"><span class="pos">#' + (i + 1) + "</span><span>" + esc(r.n) + '</span><span style="font-size:11px;color:var(--text4)">' + esc(SKINS[nivel(r.p)]!.nombre) + '</span><span class="pts">' + r.p.toLocaleString("en-US") + "</span></div>").join("");
      };
      const guardarPuntaje = async () => {
        if (score <= mejorLocal) return;
        mejorLocal = score;
        try { const r = await api.puntaje(score); mejorLocal = Math.max(mejorLocal, r.mejor); otros = (await api.ranking()).filter((x) => x.nombre !== u.nombre); pintarRanking(); }
        catch { /* el puntaje es cosmético; si falla, no se molesta al usuario */ }
      };
      const empezar = () => { score = 0; y = 0; vy = 0; obs = []; t = 0; vel = VEL0; dist = 0; proximo = 520; estado = "corriendo"; };
      const saltar = () => {
        if (estado !== "corriendo") { empezar(); return; }
        if (y === 0) vy = SALTO;
      };
      const colores = ["#4A5570", "#1470B8", "#0F9D6E", "#8A6800", "#001489"];
      const suelo = H - 44;

      function candado(o: Obs) {
        const cx = o.x + o.w / 2, top = suelo - o.h;
        // arco
        ctx.strokeStyle = "#8A6800"; ctx.lineWidth = 3; ctx.beginPath(); ctx.arc(cx, top + o.w * 0.34, o.w * 0.3, Math.PI, 0); ctx.stroke();
        // cuerpo
        ctx.fillStyle = "#F2DE96"; ctx.strokeStyle = "#8A6800"; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.roundRect(o.x, top + o.w * 0.34, o.w, o.h - o.w * 0.34, 4); ctx.fill(); ctx.stroke();
        // ojo de la cerradura
        ctx.fillStyle = "#8A6800"; ctx.beginPath(); ctx.arc(cx, top + o.w * 0.34 + (o.h - o.w * 0.34) * 0.42, 2.6, 0, Math.PI * 2); ctx.fill();
        ctx.fillRect(cx - 1.5, top + o.w * 0.34 + (o.h - o.w * 0.34) * 0.42, 3, 7);
      }
      function corredor(n: number, py: number, paso: number, enElAire: boolean) {
        const x = 90, w = 26, h = 34;
        // piernas (dos cuadros, alternan cuando corre)
        ctx.fillStyle = colores[n]!;
        if (enElAire) { ctx.fillRect(x + 5, py + h - 2, 6, 6); ctx.fillRect(x + 15, py + h - 2, 6, 6); }
        else if (paso) { ctx.fillRect(x + 3, py + h - 2, 6, 8); ctx.fillRect(x + 16, py + h - 2, 6, 4); }
        else { ctx.fillRect(x + 5, py + h - 2, 6, 4); ctx.fillRect(x + 17, py + h - 2, 6, 8); }
        // cuerpo
        ctx.beginPath(); ctx.roundRect(x, py, w, h, n === 4 ? 4 : n >= 2 ? 13 : 6); ctx.fill();
        // ojos
        ctx.fillStyle = "#fff"; ctx.fillRect(x + 15, py + 9, 5, 5); if (n === 3) ctx.fillRect(x + 6, py + 9, 5, 5);
        ctx.fillStyle = "#0B1533"; ctx.fillRect(x + 18, py + 11, 2, 2); if (n === 3) ctx.fillRect(x + 9, py + 11, 2, 2);
        if (n === 1) { ctx.fillStyle = "#fff"; ctx.fillRect(x + 8, py - 5, 10, 5); }                // antena del robot
        if (n === 4) { ctx.strokeStyle = "#fff"; ctx.lineWidth = 2.2; ctx.beginPath(); ctx.moveTo(x + 6, py + 26); ctx.lineTo(x + 6, py + 16); ctx.lineTo(x + 13, py + 21); ctx.lineTo(x + 20, py + 16); ctx.lineTo(x + 20, py + 26); ctx.stroke(); }
      }
      // El ritmo va por tiempo, no por cuadro: a 60, 120 o 30 Hz se juega igual.
      let ultimo = performance.now(), acumPuntos = 0;
      function frame(ahora = performance.now()) {
        if (!document.body.contains(cv)) return;
        const dt = Math.min(2, Math.max(0.25, (ahora - ultimo) / 16.667)); ultimo = ahora;
        ctx.clearRect(0, 0, W, H);

        if (estado === "corriendo") {
          t += dt;
          // Puntos y velocidad, como Chrome: el marcador sube despacio y la
          // velocidad crece con la distancia, no de golpe.
          acumPuntos += dt; while (acumPuntos >= 6) { acumPuntos -= 6; score += 1; }
          vel = Math.min(VELMAX, VEL0 + dist / 2200);
          dist += vel * dt;
          // Obstáculos con hueco aleatorio, más separados al principio.
          proximo -= vel * dt;
          if (proximo <= 0) {
            const w = 20 + Math.random() * 12; const h = w * 1.25 + Math.random() * 8;
            obs.push({ x: W + 10, w, h });
            proximo = 380 + Math.random() * 320 - Math.min(200, dist / 25);
          }
          vy -= GRAV * dt; y = Math.max(0, y + vy * dt); if (y === 0) vy = 0;
          obs.forEach((o) => o.x -= vel * dt); obs = obs.filter((o) => o.x > -60);
          nubes.forEach((c) => { c.x -= vel * 0.25 * dt; if (c.x < -80) c.x = W + 40; });
          const px = 92, pw = 22, ph = 34, py = suelo - ph - y;
          for (const o of obs) {
            const top = suelo - o.h;
            if (px < o.x + o.w - 3 && px + pw > o.x + 3 && py + ph > top + 4) { estado = "muerto"; void guardarPuntaje(); pintarRanking(); pintarSkins(); break; }
          }
          if (Math.floor(t) % 60 === 0) pintarSkins();
        }

        // cielo: nubes
        ctx.fillStyle = "#EDF0F5";
        nubes.forEach((c) => { ctx.beginPath(); ctx.roundRect(c.x, c.y, c.w, 12, 6); ctx.fill(); ctx.beginPath(); ctx.roundRect(c.x + 12, c.y - 8, c.w * 0.5, 12, 6); ctx.fill(); });
        // suelo con marcas que se mueven
        ctx.fillStyle = "#C2CAD6"; ctx.fillRect(0, suelo, W, 2);
        ctx.fillStyle = "#D9DFE8";
        for (let i = 0; i < 14; i++) { const x = ((i * 64) - (dist % 64) + W) % W; ctx.fillRect(x, suelo + 8, 12 + (i % 3) * 6, 2); }
        for (let i = 0; i < 9; i++) { const x = ((i * 97 + 30) - (dist * 0.7 % 97) + W) % W; ctx.fillRect(x, suelo + 16, 4, 2); }

        obs.forEach(candado);
        const n = nivel(score); const py = suelo - 34 - y;
        corredor(n, py, Math.floor(t / 7) % 2, y > 0);

        // marcador
        ctx.fillStyle = "#6E7889"; ctx.font = "600 12px system-ui, sans-serif"; ctx.textAlign = "right";
        ctx.fillText("MEJOR " + String(Math.max(score, mejorLocal)).padStart(5, "0") + "   " + String(score).padStart(5, "0"), W - 18, 26);
        ctx.textAlign = "left"; ctx.fillText(SKINS[n]!.nombre.toUpperCase(), 18, 26);

        if (estado !== "corriendo") {
          ctx.textAlign = "center"; ctx.fillStyle = "#0B1533"; ctx.font = "650 16px system-ui, sans-serif";
          ctx.fillText(estado === "muerto" ? "Un candado te frenó." : "Salta los candados.", W / 2, H / 2 - 22);
          ctx.fillStyle = "#4A5570"; ctx.font = "500 13px system-ui, sans-serif";
          ctx.fillText(estado === "muerto" ? "Espacio o clic para volver a correr" : "Pulsa espacio o haz clic para empezar", W / 2, H / 2);
        }
        requestAnimationFrame(frame);
      }
      cv.onclick = saltar;
      const tecla = (e: KeyboardEvent) => { if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement) return; if (e.code === "Space" || e.key === "ArrowUp") { e.preventDefault(); saltar(); } };
      window.addEventListener("keydown", tecla); alSalirDeVista(() => window.removeEventListener("keydown", tecla));
      pintarSkins(); pintarRanking(); frame();
      root.querySelector<HTMLButtonElement>("#jg-salir")!.onclick = () => void alSalir();
      const b = root.querySelector<HTMLButtonElement>("#jg-solicitar");
      if (b) b.onclick = () => modal({
        titulo: "Solicitar acceso", sub: "Cuéntale a IT en tu idioma. El sistema lo traduce a módulos y clientes, y propone un conjunto basado en tus pares.",
        cuerpo: '<div class="lbl">¿EN QUÉ DEPARTAMENTO TRABAJAS?</div><select id="sa-dep"><option>Cuentas</option><option>Creativo</option><option>Estrategia</option><option>Social</option><option>Finanzas</option><option>Producción</option><option>IT</option></select>' +
          '<div class="lbl">¿CON QUÉ CLIENTES VAS A TRABAJAR?</div><input id="sa-cli" placeholder="Escríbelos en texto libre — no te podemos mostrar la lista">' +
          '<div class="lbl">¿QUÉ VAS A HACER AQUÍ?</div><textarea id="sa-que" placeholder="Voy a llevar la cuenta de…"></textarea>',
        botones: [{ t: "Cancelar" }, { t: "Enviar a IT", cls: "primario", fn: async (m) => {
          const dep = (m.querySelector("#sa-dep") as HTMLSelectElement).value, cli = (m.querySelector("#sa-cli") as HTMLInputElement).value.trim(), que = (m.querySelector("#sa-que") as HTMLTextAreaElement).value.trim();
          if (!que) { (m.querySelector("#sa-que") as HTMLElement).focus(); return false; }
          await api.solicitar({ departamento: dep, clientes: cli, queHara: que });
          toast("<b>Ticket abierto.</b> IT lo ve en su bandeja. El acceso se aplica en tu siguiente entrada.");
          refrescar();
        } }],
      });
    },
  };
}
